import { useEffect, useRef } from 'react';
import { styled } from '@mui/material';
import { CaretLoadingIndicator } from '../CaretLoadingIndicator';
import environment from '@/environment';
import { trackViewEvent } from '@/services/analytics-service';
import { PrefillPayload } from '@/schemas/sceneDataSchema';

interface MediaAlphaResellProps {
  placementId: string | null;
  payload: PrefillPayload | null;
  subId: string | null;
}

const MEDIA_ALPHA_SCRIPT_ID = 'media-alpha-script';
const DEFAULT_ELEMENT_ID = 'media_alpha_element';
const DEFAULT_PLACEMENT_ID = 'ou12uf1QKlCemdHaw5-TwZCRlpA60g';

export const MediaAlphaResell = ({ placementId, payload, subId }: MediaAlphaResellProps) => {
  const isScriptAdded = useRef(false);

  function cleanupScript() {
    const script = document.getElementById(MEDIA_ALPHA_SCRIPT_ID);

    if (script) {
      document.head.removeChild(script);
    }
  }

  useEffect(() => {
    const createScript = () => {
      const existingScript = document.getElementById(MEDIA_ALPHA_SCRIPT_ID);

      if (existingScript) {
        return;
      }

      const script = document.createElement('script');
      script.id = MEDIA_ALPHA_SCRIPT_ID;
      script.src = environment.mediaAlphaUrl;
      script.onload = () => {
        window.MediaAlphaExchange = {
          data: { ...(payload ?? {}), zip: payload?.zip ?? 'auto' },
          placement_id: placementId ?? DEFAULT_PLACEMENT_ID,
          type: 'ad_unit',
          sub_1: subId,
          ua_class: 'auto',
          version: 17,
        };

        // Will overwrite the existing elements within <QuotesContainer />
        window.MediaAlphaExchange__load(DEFAULT_ELEMENT_ID);
      };

      document.head.appendChild(script);
    };

    if (isScriptAdded.current) {
      return;
    }

    createScript();

    trackViewEvent('QUOTES_LOADED', { subId, placementId: placementId ?? DEFAULT_PLACEMENT_ID }, null);

    isScriptAdded.current = true;
    return () => cleanupScript();
  }, [payload, placementId, subId]);

  return (
    <QuotesContainer
      data-testid='media-alpha-container'
      id={DEFAULT_ELEMENT_ID}
    >
      <CaretLoadingIndicator />
    </QuotesContainer>
  );
};

const QuotesContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
